// Deliberate duplicate of town-square/src/components/arty/ArtyActionsModal.tsx (ADR 0002). A change here belongs there too.
// Client divergences: react-i18next, client's `ImageManager/VisibilityChip`, and the sheet
// shell is client's Dialog-based `ArtyFullScreenSheet`.
import { useState, type ReactNode } from 'react';
import { Pencil, Search, Share2, Star, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { VisibilityChip } from '~/components/ImageManager/VisibilityChip';
import { LivePinsProvider, usePins } from '~/components/arty/actions/ArtyActionsProvider';
import { ArtyFullScreenSheet } from '~/components/arty/ArtyFullScreenSheet';
import { findAction, groupedActions, matchesQuery, type ArtyAction, type Translate } from '~/lib/arty-actions/catalog';
import { MAX_PINS } from '~/lib/arty-actions/pins';

const MAX_TAGS = 5;

const TILE_GRID = 'grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(248px,1fr))]';

const SUMMARY_BUTTON =
  'inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-[7px] text-[12.5px] font-semibold text-gray-700 hover:bg-gray-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500';

export type ArtworkSummary = {
  thumb: string | null;
  title: string | null;
  medium: string | null;
  dimensions: string | null;
  description: string | null;
  tags: { id: string; name: string }[];
  isPublic: boolean;
};

/**
 * A "product" produces a tangible thing, so its tile shows a small drawing of
 * that thing instead of a flat icon: the art book is a compact fan of pages,
 * the mockup is the piece framed on a wall, the review is a stacked report.
 */
function ProductPreview({ id, thumb }: { id: string; thumb: string | null }) {
  if (id === 'artbook') {
    return (
      <div className="relative aspect-[3/4] w-[74px]">
        <div className="absolute inset-0 origin-bottom-left rotate-[7deg] rounded-[5px] border border-gray-200 bg-white shadow-sm" />
        <div className="absolute inset-0 origin-bottom-left rotate-[3.5deg] rounded-[5px] border border-gray-200 bg-white shadow-sm" />
        <div className="absolute inset-0 overflow-hidden rounded-[5px] border border-gray-200 bg-white shadow">
          <div className="absolute inset-y-0 left-0 z-10 w-[4px] bg-black/10" />
          {thumb ? <img alt="" className="size-full object-cover" loading="lazy" src={thumb} /> : null}
        </div>
      </div>
    );
  }

  if (id === 'mockups') {
    return (
      <div className="relative h-[118px] w-full overflow-hidden rounded-lg bg-gradient-to-b from-stone-100 to-stone-200">
        <div className="absolute bottom-0 h-[22%] w-full bg-stone-300/70" />
        <div className="absolute left-1/2 top-[46%] -translate-x-1/2 -translate-y-1/2 rounded-[2px] border-[5px] border-white bg-white shadow-[0_6px_16px_rgba(0,0,0,0.2)]">
          {thumb ? (
            <img alt="" className="block max-h-[60px] w-auto object-contain" loading="lazy" src={thumb} />
          ) : (
            <div className="size-14 bg-gray-200" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-[118px] w-[88px]">
      <div className="absolute inset-0 translate-x-[5px] translate-y-[5px] rounded-[4px] border border-gray-200 bg-white" />
      <div className="absolute inset-0 translate-x-[2.5px] translate-y-[2.5px] rounded-[4px] border border-gray-200 bg-white" />
      <div className="absolute inset-0 overflow-hidden rounded-[4px] border border-gray-200 bg-white p-2.5 shadow">
        {thumb ? <img alt="" className="h-7 w-full rounded-[2px] object-cover" loading="lazy" src={thumb} /> : null}
        <div className="mt-2 space-y-[5px]">
          <div className="h-[3px] w-1/2 rounded bg-gray-300" />
          <div className="h-[2px] w-full rounded bg-gray-200" />
          <div className="h-[2px] w-full rounded bg-gray-200" />
          <div className="h-[2px] w-4/5 rounded bg-gray-200" />
          <div className="h-[2px] w-full rounded bg-gray-200" />
          <div className="h-[2px] w-2/3 rounded bg-gray-200" />
        </div>
      </div>
    </div>
  );
}

/** One tile: the run target, with the pin star in its corner. */
function ActionTile({
  action,
  preview,
  onRun,
}: {
  action: ArtyAction;
  preview?: ReactNode;
  onRun: (action: ArtyAction) => void;
}) {
  const { t } = useTranslation('arty-card');
  const { pins, atCap, toggle } = usePins();
  const pinned = pins.includes(action.id);
  const capped = !pinned && atCap;
  const Icon = action.icon;

  return (
    <div
      className={`relative rounded-2xl border p-5 transition-shadow hover:shadow-md ${
        pinned ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200 bg-white'
      }`}
      data-action-id={action.id}
    >
      <button
        aria-label={pinned ? t('sheet.unpin', { label: action.label }) : t('sheet.pin', { label: action.label })}
        aria-pressed={pinned}
        className={`absolute right-3 top-3 z-10 grid size-8 place-items-center rounded-full transition-colors hover:bg-amber-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-amber-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent ${
          pinned ? 'text-amber-400' : 'text-gray-300 hover:text-amber-400'
        }`}
        disabled={capped}
        onClick={() => {
          toggle(action.id);
        }}
        title={capped ? t('sheet.cap-hint', { max: MAX_PINS }) : undefined}
        type="button"
      >
        <Star aria-hidden="true" className={`size-5 ${pinned ? 'fill-amber-400' : ''}`} />
      </button>
      <button
        className="block w-full rounded-2xl pr-8 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500"
        onClick={() => {
          onRun(action);
        }}
        type="button"
      >
        {preview ? (
          <div className="mb-3.5 flex h-[124px] items-center justify-center">{preview}</div>
        ) : (
          <Icon aria-hidden="true" className="size-6 text-gray-700" />
        )}
        <span className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[15px] font-semibold text-gray-900">
          {action.label}
          {action.opensApp ? (
            <span className="shrink-0 rounded-md bg-emerald-100 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-emerald-700">
              {t('sheet.opens-app')}
            </span>
          ) : null}
        </span>
        <span className="mt-1.5 block text-[13px] leading-snug text-gray-500">{action.description}</span>
      </button>
    </div>
  );
}

function Summary({
  summary,
  onToggleVisibility,
  visibilityDisabled,
  onEdit,
  onShare,
}: {
  summary: ArtworkSummary;
  onToggleVisibility?: () => void;
  visibilityDisabled?: boolean;
  onEdit?: () => void;
  onShare?: () => void;
}) {
  const { t } = useTranslation('arty-card');
  const meta = [summary.medium, summary.dimensions].filter(Boolean).join(' · ');
  const shownTags = summary.tags.slice(0, MAX_TAGS);
  const extraTags = summary.tags.length - shownTags.length;

  return (
    <div className="flex flex-col gap-3 border-b border-gray-100 pb-5 sm:flex-row sm:items-start sm:gap-4">
      <div className="size-24 shrink-0 overflow-hidden rounded-xl bg-gray-100 shadow-sm">
        {summary.thumb ? <img alt="" className="size-full object-cover" loading="lazy" src={summary.thumb} /> : null}
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="text-[22px] font-semibold leading-tight tracking-tight text-gray-900">
          {summary.title || t('sheet.untitled')}
        </h2>
        {meta ? <div className="mt-1.5 text-[13px] text-gray-500">{meta}</div> : null}
        {onToggleVisibility ? (
          <div className="mt-2">
            <VisibilityChip disabled={visibilityDisabled} isPublic={summary.isPublic} onToggle={onToggleVisibility} />
          </div>
        ) : null}
        {summary.description ? (
          <p className="mt-2.5 line-clamp-2 max-w-[64ch] text-[13.5px] leading-relaxed text-gray-600">
            {summary.description}
          </p>
        ) : null}
        {shownTags.length ? (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {shownTags.map((tag) => (
              <span
                className="rounded-md border border-gray-100 bg-gray-50 px-2 py-[3px] text-[11.5px] text-gray-600"
                key={tag.id}
              >
                {tag.name}
              </span>
            ))}
            {extraTags > 0 ? (
              <span className="self-center text-[11.5px] text-gray-400">
                {t('sheet.more-tags', { count: extraTags })}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="mt-3 flex flex-wrap gap-2">
          {onEdit ? (
            <button className={SUMMARY_BUTTON} onClick={onEdit} type="button">
              <Pencil aria-hidden="true" className="size-3.5 text-gray-400" />
              {t('sheet.edit-details')}
            </button>
          ) : null}
          {onShare ? (
            <button className={SUMMARY_BUTTON} onClick={onShare} type="button">
              <Share2 aria-hidden="true" className="size-3.5 text-gray-400" />
              {t('sheet.share')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PinsHint() {
  const { t } = useTranslation('arty-card');
  const { pins, isCustom, reset } = usePins();
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-gray-500">
      <span className="inline-flex items-center gap-1">
        <Star aria-hidden="true" className="size-3.5 shrink-0 fill-amber-400 text-amber-400" />
        {t('sheet.star-hint')}
      </span>
      <span aria-hidden="true">·</span>
      <span className="font-semibold text-gray-700">{t('sheet.pinned', { count: pins.length, max: MAX_PINS })}</span>
      {isCustom ? (
        <>
          <span aria-hidden="true">·</span>
          <button
            className="rounded font-semibold text-violet-700 underline underline-offset-2 hover:text-violet-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500"
            onClick={reset}
            type="button"
          >
            {t('sheet.reset')}
          </button>
        </>
      ) : null}
    </span>
  );
}

function SearchBar({ query, onChange }: { query: string; onChange: (value: string) => void }) {
  const { t } = useTranslation('arty-card');
  return (
    <div className="sticky top-0 z-20 border-b border-gray-100 bg-white/95 px-6 py-3 backdrop-blur">
      <div className="relative max-w-[640px]">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-gray-400"
        />
        <input
          aria-label={t('sheet.search')}
          className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-10 text-[14px] text-gray-900 placeholder:text-gray-400 focus:border-violet-300 focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-violet-200"
          onChange={(event) => {
            onChange(event.target.value);
          }}
          placeholder={t('sheet.search-placeholder')}
          type="text"
          value={query}
        />
        {query ? (
          <button
            aria-label={t('sheet.clear-search')}
            className="absolute right-2.5 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-violet-300"
            onClick={() => {
              onChange('');
            }}
            type="button"
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export type ArtyActionsModalProps = {
  open: boolean;
  onClose: () => void;
  summary: ArtworkSummary;
  /** Omitted for chat attachments, which are on no profile. */
  onToggleVisibility?: () => void;
  visibilityDisabled?: boolean;
  /** Every action for this piece - the curated set plus the artist's spark library. */
  actions: ArtyAction[];
  /** True once the spark library loaded without error; until then a pin with no tile is kept. */
  sparksReady: boolean;
  /** The card's dispatcher, shared so a tile and a shortcut cannot drift. */
  onRun: (action: ArtyAction) => void;
};

/** "Everything you can do with this piece": the artwork, then every action, grouped and searchable. */
export function ArtyActionsModal({
  open,
  onClose,
  summary,
  onToggleVisibility,
  visibilityDisabled,
  actions,
  sparksReady,
  onRun,
}: ArtyActionsModalProps) {
  const { t } = useTranslation('arty-card');
  const [query, setQuery] = useState('');

  const edit = findAction(actions, 'edit');
  const share = findAction(actions, 'share');
  const sections = groupedActions(
    actions.filter((action) => {
      return matchesQuery(action, query);
    }),
    t as Translate
  );

  return (
    <LivePinsProvider actions={actions} sparksReady={sparksReady}>
      <ArtyFullScreenSheet ariaLabel={t('sheet.title')} onClose={onClose} open={open}>
        <div className="px-6 pr-16 pt-6 sm:pr-6">
          <Summary
            onEdit={
              edit
                ? () => {
                    onRun(edit);
                  }
                : undefined
            }
            onShare={
              share
                ? () => {
                    onRun(share);
                  }
                : undefined
            }
            onToggleVisibility={onToggleVisibility}
            summary={summary}
            visibilityDisabled={visibilityDisabled}
          />
          <div className="mt-4 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h3 className="text-[15px] font-semibold tracking-tight text-gray-900">{t('sheet.title')}</h3>
            <PinsHint />
          </div>
        </div>

        <SearchBar onChange={setQuery} query={query} />

        <div className="px-6 pb-11 pt-2">
          {sections.map((section) => (
            <div data-group={section.group} key={section.group}>
              <div className="mb-2.5 mt-5 text-[12px] font-semibold uppercase tracking-wide text-gray-400">
                {section.label}
              </div>
              <div className={TILE_GRID}>
                {section.actions.map((action) => (
                  <ActionTile
                    action={action}
                    key={action.id}
                    onRun={onRun}
                    preview={
                      section.group === 'products' ? <ProductPreview id={action.id} thumb={summary.thumb} /> : undefined
                    }
                  />
                ))}
              </div>
            </div>
          ))}
          {sections.length === 0 ? (
            <p className="mt-8 text-center text-[14px] text-gray-500">{t('sheet.no-match', { query: query.trim() })}</p>
          ) : null}
        </div>
      </ArtyFullScreenSheet>
    </LivePinsProvider>
  );
}
